import { useTranslation } from 'react-i18next';
import { Typography } from '@mui/material';
import { Lightbulb } from '@phosphor-icons/react';
import { useSelector } from 'react-redux';

import {
  CostDetailsBoxStyled,
  PlanPremiumBoxStyled,
  PremiumTextStyled,
  HsaEligibleBoxStyled,
  CoverageLevelsBoxStyled,
  SpaceBetweenRow,
  DetailPillStyled,
  PremiumDetailSmall,
  BoldTextStyled,
  UppercaseTextStyled,
} from './PremiumDetailsStyled';
import { BenefitPlanDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import {
  PlanLevelCostDto,
  PlanLevelCostInfoDto,
} from 'enrollment/queries/usePlanInformationQuery/utils';
import { Loader } from 'theSource/components';
import {
  convertCurrency,
  getPremiumStyled,
} from 'enrollment/utils/currency/utils';
import { selectPerPayPeriodText } from 'enrollment/services';
import { RETURN_DISPLAY } from 'enrollment/utils';
import { CategoryCoverageDto } from 'enrollment/queries/useBenefitCategoryQuery/types';

interface PremiumDetailsProps {
  plan: CategoryCoverageDto | BenefitPlanDto;
  subscriberPremium?: string;
  category?: string;
  isLoading: boolean;
  isFetching: boolean;
  planData: any;
  hideEmptyCost?: boolean;
}

export const PremiumDetails = ({
  plan,
  category,
  subscriberPremium,
  isLoading,
  isFetching,
  planData,
  hideEmptyCost = false,
}: PremiumDetailsProps) => {
  const { t } = useTranslation('enrollment.benefits');

  const perPayText = useSelector(selectPerPayPeriodText);
  const orgPlanId =
    'req_org_plan_id' in plan ? plan.req_org_plan_id : plan.plan_id;
  const premium =
    category === 'category_3' && subscriberPremium
      ? subscriberPremium
      : plan.subscriber_premium;

  const premiumDisplay =
    hideEmptyCost && Number(premium) === 0
      ? ''
      : getPremiumStyled({
          premium: premium,
          valueOnly: RETURN_DISPLAY,
        });

  const renderCoverageLevelDetail = (
    costInfo: PlanLevelCostInfoDto,
    levelLookupCode: number,
    hideEmptyCost: boolean
  ) => {
    return (
      <SpaceBetweenRow
        data-testid="coverageLevelsContainer"
        key={costInfo.level_lookup_code}
        className="coverage-levels-container"
      >
        <DetailPillStyled
          className="mobileCenter"
          $isSelected={levelLookupCode === costInfo.level_id}
        >
          {costInfo.level_name}
        </DetailPillStyled>
        <PremiumDetailSmall className="mobileCenter">
          {(!hideEmptyCost || Number(costInfo.subscriber_premium) > 0) && (
            <>
              <BoldTextStyled>
                {convertCurrency(costInfo.subscriber_premium)}
              </BoldTextStyled>
              <UppercaseTextStyled>{`${perPayText}`}</UppercaseTextStyled>
            </>
          )}
        </PremiumDetailSmall>
      </SpaceBetweenRow>
    );
  };

  return (
    <CostDetailsBoxStyled
      data-testid="premiumDetailsWrapper"
      className="premium-details-wrapper"
    >
      <PlanPremiumBoxStyled className="premium-details">
        {typeof premiumDisplay === 'object' && (
          <>
            <PremiumTextStyled data-testid="subscriberPremium">
              {premiumDisplay}
            </PremiumTextStyled>
            <Typography fontSize={14} fontWeight={400}>
              {`${perPayText}`}
            </Typography>
          </>
        )}
        {plan.high_deductible && (
          <HsaEligibleBoxStyled>
            <Lightbulb
              size={24}
              color="#d16627"
              style={{ marginRight: '20px' }}
            />
            <Typography fontSize={16}>{t('HsaEligible')}</Typography>
          </HsaEligibleBoxStyled>
        )}
      </PlanPremiumBoxStyled>
      {isLoading || isFetching ? (
        <Loader />
      ) : (
        <CoverageLevelsBoxStyled className="coverage-levels-box">
          <h2
            id={`${orgPlanId}-coverage-levels`}
            className="mobileCenter headline-3"
          >
            {t('coverageLevels')}
          </h2>

          {planData?.data.plan_level_costs &&
            Object.entries(
              planData.data.plan_level_costs as PlanLevelCostDto
            ).map((level) =>
              renderCoverageLevelDetail(
                level[1],
                'coverage_level_id' in plan
                  ? plan.coverage_level_id
                  : plan.level_id,
                hideEmptyCost
              )
            )}
        </CoverageLevelsBoxStyled>
      )}
    </CostDetailsBoxStyled>
  );
};
